import type { CustomComponentTypes } from '@ben/shared/constants';
import type {
  LifeEventRestriction,
  LifeEventRestrictionComponent,
} from '@ben/shared/types/columbia';
import { filter, get, map, set, some } from 'lodash';
import { isEmptyRestrictionSet } from '../../utils';

export interface ComponentData {
  componentId: number;
  type: CustomComponentTypes;
  name: string;
}

/**
 * Checks if the given value is a valid number greater than 0.
 * @param id - The value to check.
 * @returns True if the value is a valid number greater than 0, false otherwise.
 * @context This is used to validate the stepId and componentId are not null or undefined.
 */
export const isValidId = (id: unknown): id is number =>
  typeof id === 'number' && id > 0;

export const isValidRestrictionEntry = (
  restriction: LifeEventRestriction | undefined
): restriction is LifeEventRestriction =>
  restriction !== undefined &&
  isValidId(restriction.stepId) &&
  Array.isArray(restriction.components);

export const isValidComponentEntry = (
  component: LifeEventRestrictionComponent | undefined
): component is LifeEventRestrictionComponent =>
  component !== undefined &&
  isValidId(component.componentId) &&
  typeof component.type === 'string' &&
  component.type.length > 0;

export const cleanComponentRestrictions = (
  component: LifeEventRestrictionComponent
): LifeEventRestrictionComponent => {
  const { restrictionSet } = component;

  if (!restrictionSet || isEmptyRestrictionSet(restrictionSet)) {
    return { ...component, restrictionSet: {} };
  }

  return component;
};

/**
 * Deduplicates life event restrictions by stepId and merges their components.
 *
 * This function processes an array of life event restrictions and:
 * 1. Removes invalid restriction entries (missing stepId or components array)
 * 2. Consolidates restrictions with duplicate stepIds into a single entry
 * 3. Merges components from duplicate restrictions, avoiding component duplicates
 * 4. Filters out invalid components (missing componentId, type, or name)
 * 5. Cleans component restrictions by removing empty restriction sets
 *
 * @param restrictions - Array of life event restrictions that may contain duplicates
 * @returns Deduplicated array of life event restrictions with merged components
 *
 * @example
 * const restrictions = [
 *   { stepId: 1, components: [{ componentId: 10, ... }] },
 *   { stepId: 1, components: [{ componentId: 20, ... }] },
 *   { stepId: 2, components: [{ componentId: 30, ... }] }
 * ];
 * const result = deduplicateRestrictions(restrictions);
 * // Returns:
 * // [
 * //   { stepId: 1, components: [{ componentId: 10, ... }, { componentId: 20, ... }] },
 * //   { stepId: 2, components: [{ componentId: 30, ... }] }
 * // ]
 */
export const deduplicateRestrictions = (
  restrictions: LifeEventRestriction[]
): LifeEventRestriction[] => {
  const seen = new Map<number, LifeEventRestriction>();

  for (const restriction of restrictions) {
    if (!isValidRestrictionEntry(restriction)) continue;

    const existingRestriction = seen.get(restriction.stepId);

    if (existingRestriction) {
      const mergedComponents = [...existingRestriction.components];

      for (const component of restriction.components) {
        const isDuplicate = some(
          mergedComponents,
          (existingComponent) =>
            existingComponent.componentId === component.componentId
        );

        if (isValidComponentEntry(component) && !isDuplicate) {
          mergedComponents.push(cleanComponentRestrictions(component));
        }
      }

      seen.set(restriction.stepId, {
        ...existingRestriction,
        components: mergedComponents,
      });
    } else {
      const validComponents = map(
        filter(restriction.components, isValidComponentEntry),
        cleanComponentRestrictions
      );

      seen.set(restriction.stepId, {
        ...restriction,
        components: validComponents,
      });
    }
  }

  return [...seen.values()];
};

export const createComponentEntry = (
  componentData: ComponentData
): LifeEventRestrictionComponent => ({
  componentId: componentData.componentId,
  type: componentData.type,
  name: componentData.name,
  restrictionApplied: true,
  restrictionSet: {},
});

export const ensureStepExists = (
  restrictions: LifeEventRestriction[],
  stepId: number
): number => {
  const existingStepIndex = restrictions.findIndex(
    (restriction) => restriction.stepId === stepId
  );

  if (existingStepIndex !== -1) {
    return existingStepIndex;
  }

  const newRestriction: LifeEventRestriction = {
    stepId,
    components: [],
  };

  restrictions.push(newRestriction);

  return restrictions.length - 1;
};

export const ensureComponentExists = (
  restrictions: LifeEventRestriction[],
  stepIndex: number,
  componentData: ComponentData,
  withSubComponents = false
): number => {
  const componentsPath = `${stepIndex}.components`;
  const componentsList = get(restrictions, componentsPath, []);

  const existingComponentIndex = componentsList.findIndex(
    (component: LifeEventRestrictionComponent) =>
      component.componentId === componentData.componentId
  );

  if (existingComponentIndex !== -1) {
    return existingComponentIndex;
  }

  const newComponent: LifeEventRestrictionComponent = withSubComponents
    ? { ...createComponentEntry(componentData), subComponents: [] }
    : createComponentEntry(componentData);
  const newComponentIndex = componentsList.length;

  set(restrictions, `${componentsPath}.${newComponentIndex}`, newComponent);

  return newComponentIndex;
};

export const ensureSubComponentExists = (
  restrictions: LifeEventRestriction[],
  stepIndex: number,
  parentIndex: number,
  subComponentData: ComponentData
): number => {
  const subComponentsPath = `${stepIndex}.components.${parentIndex}.subComponents`;
  const subComponentsList = get(restrictions, subComponentsPath, []);

  const existingSubComponentIndex = subComponentsList.findIndex(
    (subComponent: LifeEventRestrictionComponent) =>
      subComponent.componentId === subComponentData.componentId
  );

  if (existingSubComponentIndex !== -1) {
    return existingSubComponentIndex;
  }

  const newSubComponent = createComponentEntry(subComponentData);
  const newSubComponentIndex = subComponentsList.length;

  set(
    restrictions,
    `${subComponentsPath}.${newSubComponentIndex}`,
    newSubComponent
  );

  return newSubComponentIndex;
};

export const cleanupRestrictions = (
  restrictions: LifeEventRestriction[]
): LifeEventRestriction[] => {
  const deduplicatedRestrictions = deduplicateRestrictions(restrictions);

  const cleanedRestrictions = map(deduplicatedRestrictions, (currentStep) => {
    const validComponents = map(
      filter(currentStep.components, isValidComponentEntry),
      (component) => {
        const subComponentsList = get(component, 'subComponents', []);
        const validSubComponents = filter(
          subComponentsList,
          isValidComponentEntry
        );
        const cleanedSubComponents = map(
          validSubComponents,
          cleanComponentRestrictions
        );

        return {
          ...cleanComponentRestrictions(component),
          subComponents:
            cleanedSubComponents.length > 0 ? cleanedSubComponents : undefined,
        };
      }
    );

    return {
      ...currentStep,
      components: validComponents,
    };
  });

  return filter(
    cleanedRestrictions,
    (currentStep) => currentStep.components.length > 0
  );
};
